import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories } from '@/mocks/categories';
import { getArticlesByCategory } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const currentCategory = categories.find((c) => c.slug === slug);
  const articleList = slug ? getArticlesByCategory(slug) : [];

  if (!currentCategory) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="min-h-screen pt-16 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-heading font-semibold text-foreground-900 mb-3">分类未找到</h1>
            <Link to="/" className="text-sm text-primary-500 hover:text-primary-600">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <ScrollToTop />
      <Navbar />

      <main className="min-h-screen pt-16">
        <div className="bg-background-50">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: currentCategory.name },
            ]}
          />
        </div>

        <section className="w-full py-8 md:py-12 px-4 md:px-6 bg-background-50">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-8 md:mb-10">
              <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-xl bg-primary-100/70 text-primary-500 mb-4">
                <i className={`${currentCategory.icon} text-2xl`}></i>
              </div>
              <h1 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-900 mb-2">
                {currentCategory.name}
              </h1>
              <p className="text-sm md:text-base text-foreground-600 max-w-lg mx-auto leading-relaxed">
                {currentCategory.description}
              </p>
            </div>

            <div className="flex flex-wrap items-center justify-center gap-2 mb-10">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                    cat.slug === slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200 hover:text-foreground-800'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>

            {articleList.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-foreground-500">该分类下暂无文章</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
                {articleList.map((article) => (
                  <Link
                    key={article.slug}
                    to={`/article/${article.slug}`}
                    className="group flex flex-col sm:flex-row bg-background-100 rounded-lg border border-background-200/70 hover:border-primary-200 overflow-hidden transition-all duration-300 cursor-pointer"
                  >
                    <div className="w-full sm:w-44 md:w-52 h-40 sm:h-auto flex-shrink-0 overflow-hidden">
                      <img
                        src={article.imageUrl}
                        alt={article.title}
                        title={article.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-4 md:p-5 flex flex-col flex-1 justify-center">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs text-foreground-500">{article.readTime}</span>
                        <span className="text-xs text-foreground-400">{article.date}</span>
                      </div>
                      <h3 className="text-base font-heading font-semibold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors duration-200 line-clamp-2 leading-snug">
                        {article.title}
                      </h3>
                      <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2 mb-2">
                        {article.excerpt}
                      </p>
                      <span className="text-xs text-foreground-500">{article.author}</span>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: `${currentCategory.name} - 色彩美学`,
            description: currentCategory.description,
            url: `https://color-aesthetics.com/category/${slug}`,
          }),
        }}
      />
    </>
  );
}